// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * API endpoint for modifying existing event groups.
 * Provides functionality to update multi-step processes like shipping
 * or maintenance routines, allowing changes to events, properties,
 * and location information while maintaining process integrity.
 */
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { CreateEventGroupParameters } from '@/asset/api/create-event-group';
import { eventGroupSchema } from '@/asset/api/types';
import { serializeDateForAPI } from '@/utilities/date-time';
import { z } from 'zod';

/**
 * Schema for validating the API response when updating an event group.
 * Transforms the response to provide a more intuitive structure with
 * the event group data separated from metadata.
 */
const updateEventGroupResponseSchema = z
  .object({
    data: eventGroupSchema,
    metadata: responseMetadataSchema,
  })
  .transform(({ data, ...response }) => ({ ...response, eventGroup: data }));

type UpdateEventGroupResponse = z.infer<typeof updateEventGroupResponseSchema>;

/**
 * Updates an existing event group with modified information.
 * This function handles the API call to modify multi-step processes
 * and validates the response data using Zod schemas. It supports
 * updating event details, properties, and location information while
 * maintaining the integrity of the process sequence.
 *
 * @param assetKey - Unique identifier of the asset containing the event group
 * @param eventGroupKey - Unique identifier of the event group to update
 * @param parameters - Object containing the updated event group details
 * @returns Promise resolving to the updated event group data and request metadata
 * @throws Will throw an error if the API request fails or if the response data is invalid
 */
export async function updateEventGroup(
  assetKey: string,
  eventGroupKey: string,
  parameters: CreateEventGroupParameters
): Promise<UpdateEventGroupResponse> {
  const url = `/v1/asset/${assetKey}/event-group/${eventGroupKey}`;
  const response = await api.patch(url, {
    events: parameters.events.map((event) => ({
      ...event,
      occurredAt:
        event.occurredAt === undefined || event.occurredAt === null
          ? event.occurredAt
          : event.occurredAt.toUTC().toISO(),
      expectedAt:
        event.expectedAt === undefined || event.expectedAt === null
          ? event.expectedAt
          : event.expectedAt.toUTC().toISO(),
      promiseDate: serializeDateForAPI(event.promiseDate),
    })),
    originId: parameters.originId,
    destinationId: parameters.destinationId,
  });

  return parseResponse(updateEventGroupResponseSchema, response.data, {
    schemaName: 'UpdateEventGroup',
    endpoint: url,
  });
}
